import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation, useNavigate, useRoutes } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider, useToast } from "./context/ToastContext";
import { ConfirmProvider } from "./context/ConfirmContext";
import { getBranding, getBrandingLogoUrl, getDashboardSummary, getBillingUsage, getOnboardingChecklist, getNotifications, getUnreadCount, markNotificationRead, markAllNotificationsRead, getAnnouncements, markAnnouncementRead } from "./api";
import { useModules } from "./hooks/useModules";
import AppNav, { NavItem } from "./components/AppNav";
import { buildShellConfig, Guard, RoleKey, ShellCtx } from "./shellConfig";
import { ModuleKey, ModuleMap, CORE_LABEL, MODULE_LABELS, MODULE_COLORS, MODULE_HOME_PATHS, MODULE_KEYS, moduleForPath, modulesForRoutes, ownsModule } from "./moduleMetadata";
import { AccessDenied } from "./components/shellPrimitives";
import ChatWidget from "./components/ChatWidget";
import ProductSwitcher from "./components/ProductSwitcher";
import ModuleUpsellPage from "./pages/ModuleUpsellPage";
import { SENDER_ROLES } from "./pages/EsignPage";
import ExpenseCaptureDashboardPage from "./pages/ExpenseCaptureDashboardPage";
import FinanceDashboardPage from "./pages/FinanceDashboardPage";
import RecruitmentDashboardPage from "./pages/RecruitmentDashboardPage";
import PayrollDashboardPage from "./pages/PayrollDashboardPage";
import EsignDashboardPage from "./pages/EsignDashboardPage";
import PerformanceDashboardPage from "./pages/PerformanceDashboardPage";
import { rememberLastModule, resolveLandingModule } from "./landingPreference";
import { useActiveModule } from "./useActiveModule";
import "./responsive.css";
import LandingPage from "./pages/LandingPage";
import SignupPage from "./pages/SignupPage";
import RequestAccessPage from "./pages/RequestAccessPage";
import SignupSuccessPage from "./pages/SignupSuccessPage";
import InvitePage from "./pages/InvitePage";
import LoginPage from "./pages/LoginPage";
import SuperAdminShell from "./pages/SuperAdminShell";
import EnrollmentGatePage from "./pages/EnrollmentGatePage";
import SetupLinkPage from "./pages/SetupLinkPage";
import PricingPage from "./pages/PricingPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import DocsIndex from "./pages/docs/DocsIndex";
import CareersPage from "./pages/CareersPage";
import ScreenPublicPage from "./pages/ScreenPublicPage";
import InterviewConfirmPage from "./pages/InterviewConfirmPage";
import InterviewAvailabilityPage from "./pages/InterviewAvailabilityPage";
import OfferAcceptPage from "./pages/OfferAcceptPage";
import OfferApprovalPage from "./pages/OfferApprovalPage";
import PublicEsignSignPage from "./pages/PublicEsignSignPage";
import DocsGettingStarted from "./pages/docs/GettingStarted";
import DocsAIAssistant from "./pages/docs/AIAssistant";
import DocsRecruitment from "./pages/docs/Recruitment";
import DocsPeopleHR from "./pages/docs/PeopleHR";
import DocsFinance from "./pages/docs/Finance";
import DocsAdminSettings from "./pages/docs/AdminSettings";

const qc = new QueryClient();

const PAGE_NAMES: Record<string, string> = {
  "/":            "Dashboard",
  // Module dashboards. Without these the header bar renders blank on them.
  "/recruitment":      "Recruitment",
  "/finance":          "Finance",
  "/payroll-home":     "Payroll",
  "/performance-home": "Performance",
  "/esign-home":       "eSign",
  "/expenses-home":    "AI Expense Capture",
  "/jobs":        "Jobs",
  "/candidates":  "Candidates",
  "/offers":      "Offers",
  "/leaves":      "Leaves",
  "/my-leaves":   "My Leaves",
  "/employees":   "Employees",
  "/attendance":  "Attendance",
  "/performance": "Performance",
  "/org-chart":   "Org Chart",
  "/exit":        "Offboarding",
  "/people-reports": "Reports",
  "/recruitment-reports": "Recruitment Reports",
  "/recruitment-reports/time-to-hire": "Recruitment Reports",
  "/recruitment-reports/sources": "Recruitment Reports",
  "/recruitment-reports/interviews": "Recruitment Reports",
  "/recruitment-reports/offers": "Recruitment Reports",
  "/recruitment-reports/pipeline": "Recruitment Reports",
  "/recruitment-reports/screening": "Recruitment Reports",
  "/people-reports/movement": "Reports",
  "/people-reports/attrition": "Reports",
  "/people-reports/org": "Reports",
  "/people-reports/leave": "Reports",
  "/people-reports/liability": "Reports",
  "/people-reports/absence": "Reports",
  "/payroll/reports": "Payroll Reports",
  "/assets":      "Assets",
  "/payroll":     "Payroll",
  "/my-slips":    "Salary Slips",
  "/invoices":       "Invoices (Legacy)",
  "/vendors":        "Vendors",
  "/vendor-bills":   "Vendor Bills",
  "/vendor-bills/bulk-upload": "Bulk Receipt Upload",
  "/ai-expense-capture": "AI Expense Capture",
  "/products":       "Products",
  "/quotations":     "Quotations",
  "/billing-notes":  "Billing Notes",
  "/tax-invoices":   "Tax Invoices",
  "/receipts":       "Receipts",
  "/debit-credit-notes": "Credit / Debit Notes (Legacy)",
  "/sales-credit-debit-notes": "Credit / Debit Notes",
  "/expenses":    "Reimbursements",
  // Module sub-pages. Each nav link is its own page, so each needs its own
  // title — falling back to the module's base title made the header read
  // "Payroll" on all four Payroll pages, and eSign (which had no base entry at
  // all) rendered a blank header on every one of its four.
  "/payroll/structure":   "Salary Structure",
  "/payroll/settings":    "Payroll Settings",
  "/payroll/exports":     "Payroll Exports",
  "/esign/templates":     "Templates",
  "/esign/sent":          "Sent Documents",
  "/esign/to-sign":       "To Sign",
  "/to-sign":             "To Sign",
  "/ai-expense-capture/ledger":   "Expense Ledger",
  "/ai-expense-capture/branches": "Branches",
  "/ai-expense-capture/insights": "Spend Insights",
  "/ai-expense-capture/settings": "AI Expense Settings",
  "/performance/goals":     "My Goals",
  "/performance/check-ins": "Check-ins",
  "/performance/cycles":    "Manage Cycles",
  "/performance/analytics": "Performance Analytics",
  // Pre-existing blanks, unrelated to the module split — these pages have
  // always rendered an empty header bar.
  "/talent-pool":           "Talent Pool",
  "/interview-hub":         "Interview Hub",
  "/screening":             "AI Screening",
  "/recruiter-preferences": "Recruiter Preferences",
  "/timesheet":             "Timesheet",
  "/people-settings":       "People Settings",
  "/announcements":         "Announcements",
  "/letters":               "Letters",
  "/helpdesk":              "Helpdesk",
  "/referrals":             "Referrals",
  "/pulse":                 "Pulse Survey",
  "/signature-register": "Signature Register",
  "/settings":          "Settings",
  "/workspace":         "Workspace",
  "/security":          "Security",
  "/policies":          "Company Policies",
};

const PAGE_SUBTITLES: Record<string, string> = {
  "/":            "Overview of your workspace",
  "/recruitment":      "Hiring pipeline at a glance",
  "/finance":          "Billing, invoicing and spend",
  "/payroll-home":     "Pay runs and payslips",
  "/performance-home": "Goals, reviews and feedback",
  "/esign-home":       "Documents out for signature",
  "/to-sign":          "Documents waiting on your signature",
  "/expenses-home":    "Receipt capture and spend insights",
  "/jobs":        "Manage open positions",
  "/candidates":  "Track applicants and pipeline",
  "/offers":      "AI-generated offer letters",
  "/leaves":      "Approve and manage leave requests",
  "/my-leaves":   "Your leave balance and requests",
  "/employees":   "Employee directory",
  "/attendance":  "Track attendance records",
  "/performance": "Review cycles, self-assessments and manager feedback",
  "/org-chart":   "Visualise your team structure",
  "/exit":        "Resignation, clearance checklist and final settlement",
  "/people-reports": "Headcount and leave utilization",
  "/recruitment-reports": "Hiring pipeline analytics",
  "/recruitment-reports/time-to-hire": "Hiring pipeline analytics",
  "/recruitment-reports/sources": "Hiring pipeline analytics",
  "/recruitment-reports/interviews": "Hiring pipeline analytics",
  "/recruitment-reports/offers": "Hiring pipeline analytics",
  "/recruitment-reports/pipeline": "Hiring pipeline analytics",
  "/recruitment-reports/screening": "Hiring pipeline analytics",
  "/people-reports/movement": "People analytics",
  "/people-reports/attrition": "People analytics",
  "/people-reports/org": "People analytics",
  "/people-reports/leave": "People analytics",
  "/people-reports/liability": "People analytics",
  "/people-reports/absence": "People analytics",
  "/payroll/reports": "What payroll cost over the period",
  "/assets":      "Company equipment and device assignment",
  "/payroll":     "Run payroll and issue salary slips",
  "/my-slips":    "Your salary slip history",
  "/invoices":       "Legacy client invoicing — superseded by Quotations → Billing Notes → Tax Invoices for new work",
  "/vendors":        "Suppliers and service providers the company pays",
  "/vendor-bills":   "Accounts payable — bills received from vendors",
  "/ai-expense-capture": "Snap or upload a receipt — AI extracts the details, you review and save",
  "/products":       "Product / service catalog used to fill line items on Quotations, Billing Notes, and Tax Invoices",
  "/quotations":     "Quotations — convert to Billing Note or Tax Invoice",
  "/billing-notes":  "Billing notes — convert to Tax Invoice or Receipt",
  "/tax-invoices":   "Tax invoices — mark as paid to generate a receipt",
  "/receipts":       "Receipts — proof of payment",
  "/debit-credit-notes": "Credit and debit notes against legacy Invoices",
  "/sales-credit-debit-notes": "Credit/debit notes against Tax Invoices",
  "/expenses":    "Out-of-pocket expense claims and reimbursements",
  "/payroll/structure":   "Pay components and CTC breakdown per employee",
  "/payroll/settings":    "Statutory rates, deductions and pay rules",
  "/payroll/exports":     "Bank transfer files and statutory reports",
  "/esign/templates":     "Reusable documents with signature fields already placed",
  "/esign/sent":          "Track what you have sent out for signature",
  "/esign/to-sign":       "Documents waiting on your signature",
  "/ai-expense-capture/ledger":   "Every captured expense, searchable and filterable",
  "/ai-expense-capture/branches": "Captured spend split by branch",
  "/ai-expense-capture/insights": "Spend trends and AI-generated narrative",
  "/ai-expense-capture/settings": "Extraction rules and expense categories",
  "/performance/goals":     "Your objectives and key results",
  "/performance/check-ins": "Lightweight 1:1 notes with your manager",
  "/performance/cycles":    "Create and run review cycles",
  "/performance/analytics": "Completion rates and rating distribution",
  "/talent-pool":           "Candidates saved for future roles",
  "/interview-hub":         "Schedule and track interviews",
  "/screening":             "AI-scored CV and video screening",
  "/recruiter-preferences": "Company watch list, CV intake, scoring and screening defaults, and applicant notifications",
  "/timesheet":             "Log and submit hours worked",
  "/people-settings":       "Attendance capture and timesheet configuration",
  "/announcements":         "Company-wide announcements",
  "/letters":               "Employment letters and certificates",
  "/helpdesk":              "Raise and track internal support tickets",
  "/referrals":             "Refer candidates and track referral status",
  "/pulse":                 "Short, recurring employee sentiment surveys",
  "/signature-register": "Every document signed, declined or voided",
  "/settings":          "Company and system settings",
  "/workspace":         "Team and workspace management",
  "/security":    "Authentication and security settings",
  "/policies":    "Browse and download company policy documents",
};

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQuery({ queryKey: ["notif-count"], queryFn: getUnreadCount, refetchInterval: 60000 });
  const nq = useQuery({ queryKey: ["notifications"], queryFn: getNotifications, enabled: open });
  const count = (qc.data as any)?.count || 0;
  const items: any[] = (nq.data as any) || [];

  function timeAgo(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  async function handleRead(id: number) {
    await markNotificationRead(id);
    qc.refetch(); nq.refetch();
  }

  async function handleReadAll() {
    await markAllNotificationsRead();
    qc.refetch(); nq.refetch();
  }

  return (
    <div style={{ position: "relative", marginRight: 8 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        position: "relative", background: "none", border: "none", cursor: "pointer",
        padding: 8, borderRadius: 8, color: "#64748b",
        display: "flex", alignItems: "center",
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {count > 0 && (
          <span style={{
            position: "absolute", top: 4, right: 4, background: "#ef4444", color: "white",
            borderRadius: "50%", width: 16, height: 16, fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
          }}>{count > 9 ? "9+" : count}</span>
        )}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
          <div style={{
            position: "absolute", right: 0, top: "calc(100% + 8px)", width: 360, maxHeight: 480,
            background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
            border: "1px solid #e2e8f0", zIndex: 200, overflow: "hidden", display: "flex", flexDirection: "column",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Notifications {count > 0 && <span style={{ color: "#ef4444" }}>({count})</span>}</span>
              {count > 0 && <button onClick={handleReadAll} style={{ background: "none", border: "none", color: "#1e3a8a", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Mark all read</button>}
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {items.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No notifications yet</div>
              ) : items.map((n: any) => (
                <div key={n.id} onClick={() => handleRead(n.id)} style={{
                  padding: "12px 16px", borderBottom: "1px solid #f8fafc", cursor: "pointer",
                  background: n.is_read ? "white" : "#f0f7ff",
                  display: "flex", gap: 10, alignItems: "flex-start",
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%", marginTop: 6, flexShrink: 0,
                    background: n.is_read ? "#e2e8f0" : "#1e3a8a",
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>{n.title}</div>
                    {n.message && <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>{n.message}</div>}
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{n.created_at ? timeAgo(n.created_at) : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ShellHeader({ eligibleModules }: { eligibleModules?: Set<ModuleKey> }) {
  const { user, logout, refreshAuth } = useAuth();
  const location = useLocation();
  const activeModule = useActiveModule();
  const { modules: switcherModules } = useModules();
  const [open, setOpen] = useState(false);
  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const signatureInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [localAvatar, setLocalAvatar] = useState<string | null>(null);
  const [localSignature, setLocalSignature] = useState<string | null>(null);

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Show preview instantly via blob URL
    const blobUrl = URL.createObjectURL(file);
    setLocalAvatar(blobUrl);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:30801"}/api/employees/me/photo`, {
        method: "POST", credentials: "include", body: fd,
      });
      // Sync with server so the persisted URL is used on next load
      await refreshAuth();
    } finally {
      setUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  }

  // Used as the signature stamp on documents this user approves (e.g. offer
  // letters, spec: .claude/specs/offer-letter-signatures.md).
  async function handleSignatureUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const blobUrl = URL.createObjectURL(file);
    setLocalSignature(blobUrl);
    setUploadingSignature(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:30801"}/api/employees/me/signature`, {
        method: "POST", credentials: "include", body: fd,
      });
      await refreshAuth();
    } finally {
      setUploadingSignature(false);
      if (signatureInputRef.current) signatureInputRef.current.value = "";
    }
  }

  const API = process.env.REACT_APP_API_URL || "http://localhost:30801";
  const avatarSrc = localAvatar || (user?.picture ? `${API}${user.picture}` : null);
  const signatureSrc = localSignature || (user?.signature_url ? `${API}${user.signature_url}` : null);

  let pageName = PAGE_NAMES[location.pathname] ?? "";
  let pageSub  = PAGE_SUBTITLES[location.pathname] ?? "";
  if (location.pathname === "/esign") {
    // One URL, two pages: EsignPage sends senders to Send for Signature and
    // everyone else to their To Sign list, so the title has to follow the role
    // rather than sit in the static map.
    const sender = SENDER_ROLES.includes(user?.role);
    pageName = sender ? "Send for Signature" : "To Sign";
    pageSub = sender
      ? "Send a document out for signature"
      : "Documents waiting on your signature";
  }
  else if (!pageName && location.pathname.startsWith("/jobs/new")) { pageName = "New Job"; pageSub = "Create a new job posting"; }
  else if (!pageName && location.pathname.startsWith("/jobs/")) { pageName = "Job Details"; pageSub = ""; }
  else if (!pageName) {
    // Anything still unmatched (a detail route, not a nav destination) titles
    // as its base page rather than rendering a blank header. Nav paths proper
    // are covered exhaustively by PAGE_NAMES — scripts/check_nav_config.py
    // fails the build if one is missing, so this is no longer load-bearing for
    // module sub-pages the way it briefly was.
    const base = "/" + location.pathname.split("/")[1];
    pageName = PAGE_NAMES[base] ?? "";
    pageSub = PAGE_SUBTITLES[base] ?? "";
  }

  const roleLabel: Record<string, string> = {
    admin: "Admin", admin_assistant: "Admin Assistant",
    hr: "HR", recruiter: "Recruiter", finance: "Finance", employee: "Employee", contractor: "Contractor",
  };

  return (
    <div className="shell-header" style={{
      position: "fixed", top: 0, left: 224, right: 0, height: 64,
      background: "#ffffff",
      borderBottom: "1px solid #e2e8f0",
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 32px",
      zIndex: 150,
    }}>
      {/* Left: page title */}
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{pageName}</div>
        {pageSub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{pageSub}</div>}
      </div>

      {/* Right: launcher + notifications + avatar */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <ProductSwitcher
        modules={switcherModules}
        eligible={eligibleModules}
        activeModule={activeModule}
        userId={user?.id}
        anchor="right"
      />
      <NotificationBell />
      <div style={{ position: "relative" }}>
        {/* File input lives OUTSIDE the conditional dropdown so it survives setOpen(false) */}
        <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={handlePhotoUpload} />
        <input ref={signatureInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={handleSignatureUpload} />
        <div
          onClick={() => setOpen(o => !o)}
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
        >
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{user?.name || "User"}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{roleLabel[user?.role] || user?.role || ""}</div>
          </div>
          {avatarSrc ? (
            <img src={avatarSrc} alt="" style={{ width: 34, height: 34, borderRadius: "50%", border: "2px solid #e2e8f0", objectFit: "cover" }} />
          ) : (
            <div style={{
              width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(135deg, #1e3a8a, #0ea5e9)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontWeight: 700, fontSize: 13,
              border: "2px solid #e2e8f0",
            }}>
              {(user?.name || "U")[0].toUpperCase()}
            </div>
          )}
        </div>

        {open && (
          <>
            {/* backdrop */}
            <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
            {/* dropdown */}
            <div style={{
              position: "absolute", top: 44, right: 0, zIndex: 201,
              background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 180, overflow: "hidden",
            }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ position: "relative", cursor: "pointer" }} onClick={() => { photoInputRef.current?.click(); }}>
                  {avatarSrc ? (
                    <img src={avatarSrc} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid #e2e8f0" }} />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg, #1e3a8a, #0ea5e9)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 15 }}>
                      {(user?.name || "U")[0].toUpperCase()}
                    </div>
                  )}
                  <div style={{ position: "absolute", bottom: -1, right: -1, width: 16, height: 16, background: "#1e3a8a", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "white" }}>✎</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{user?.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{user?.email}</div>
                  <div style={{ fontSize: 10, color: "#00c9a7", marginTop: 1, cursor: "pointer" }} onClick={() => { photoInputRef.current?.click(); }}>
                    {uploading ? "Uploading…" : "Change photo"}
                  </div>
                </div>
              </div>
              <div
                onClick={() => signatureInputRef.current?.click()}
                style={{
                  padding: "10px 16px", borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 10,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                onMouseLeave={e => (e.currentTarget.style.background = "none")}
              >
                {signatureSrc ? (
                  <img src={signatureSrc} alt="" style={{ width: 40, height: 20, objectFit: "contain", border: "1px solid #e2e8f0", borderRadius: 4, background: "#fff" }} />
                ) : (
                  <div style={{ width: 40, height: 20, border: "1px dashed #cbd5e1", borderRadius: 4 }} />
                )}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>My Signature</div>
                  <div style={{ fontSize: 10, color: "#00c9a7" }}>
                    {uploadingSignature ? "Uploading…" : signatureSrc ? "Change signature" : "Upload signature"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setOpen(false); logout(); }}
                style={{
                  width: "100%", padding: "11px 16px", background: "none", border: "none",
                  textAlign: "left", fontSize: 13, color: "#dc2626", cursor: "pointer",
                  fontWeight: 600, display: "flex", alignItems: "center", gap: 8,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = "#fef2f2")}
                onMouseLeave={e => (e.currentTarget.style.background = "none")}
              >
                ⏻ Sign out
              </button>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
}

// ── Pinned announcement banner ────────────────────────────────────────────────

const PINNED_CATEGORY_COLORS: Record<string, { accent: string; bg: string }> = {
  urgent:  { accent: "#dc2626", bg: "#fef2f2" },
  policy:  { accent: "#7c3aed", bg: "#f5f3ff" },
  event:   { accent: "#0891b2", bg: "#ecfeff" },
  holiday: { accent: "#059669", bg: "#ecfdf5" },
  general: { accent: "#1e3a8a", bg: "#eff6ff" },
};

function PinnedAnnouncementBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const { data: all = [] } = useQuery({
    queryKey: ["announcements-all"],
    queryFn: () => getAnnouncements(""),
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  if (location.pathname === "/announcements") return null;

  const pinned = (all as any[]).filter(
    (a: any) => a.pinned && !a.is_read && !dismissed.has(a.id)
  );
  if (pinned.length === 0) return null;

  const current = pinned[0];
  const more = pinned.length - 1;
  const { accent, bg } = PINNED_CATEGORY_COLORS[current.category] || PINNED_CATEGORY_COLORS.general;

  async function dismiss(id: number) {
    setDismissed(prev => new Set([...prev, id]));
    try { await markAnnouncementRead(id); } catch {}
  }

  return (
    <div style={{
      borderLeft: `4px solid ${accent}`, background: bg, borderRadius: 10,
      padding: "10px 16px", marginBottom: 16,
      display: "flex", alignItems: "center", gap: 12,
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>📌</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{current.title}</span>
        <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>
          {current.body.length > 90 ? current.body.substring(0, 90) + "…" : current.body}
        </span>
      </div>
      {more > 0 && (
        <span style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap", flexShrink: 0 }}>
          +{more} more
        </span>
      )}
      <button onClick={() => navigate("/announcements")} style={{
        background: accent, color: "white", border: "none",
        padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
        cursor: "pointer", flexShrink: 0,
      }}>View</button>
      <button onClick={() => dismiss(current.id)} title="Mark as read" style={{
        background: "none", border: "none", cursor: "pointer",
        color: "#94a3b8", fontSize: 18, flexShrink: 0, lineHeight: 1, padding: "0 2px",
      }}>✕</button>
    </div>
  );
}

// ── Plan banner (trial / grace / readonly) ────────────────────────────────────

const _BANNER: Record<string, { bg: string; border: string; color: string }> = {
  grace:    { bg: "#fefce8", border: "#fde047", color: "#854d0e" },
  readonly: { bg: "#fef2f2", border: "#fca5a5", color: "#991b1b" },
  trial:    { bg: "#eff6ff", border: "#93c5fd", color: "#1e40af" },
};

function PlanBanner() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["billing-usage"],
    queryFn: getBillingUsage,
    enabled: !!user && !user.is_super_admin,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!data || data.is_grandfathered) return null;

  // Priority 1: account status banners
  let msg = "";
  let key = "";

  if (data.effective_status === "readonly") {
    key = "readonly";
    msg = "Your account is in read-only mode. Upgrade your plan to create or edit data.";
  } else if (data.effective_status === "grace") {
    key = "grace";
    msg = `Trial ended — you have ${data.grace_days_left ?? 0} day${(data.grace_days_left ?? 0) !== 1 ? "s" : ""} of full access left. Upgrade now to keep access.`;
  } else if (
    // Covers both the no-card trial (plan==="trial", effective_status "active") and the
    // self-serve card-authorized trial (real plan tier, effective_status "trialing") —
    // trial_days_left is set by the backend for both, so that alone is the real signal.
    (data.effective_status === "active" || data.effective_status === "trialing") &&
    data.trial_days_left != null && data.trial_days_left <= 7
  ) {
    key = "trial";
    msg = data.effective_status === "trialing" && data.trial_days_left > 0
      ? `Your card will be charged when your trial ends in ${data.trial_days_left} day${data.trial_days_left !== 1 ? "s" : ""}. Cancel anytime before then to avoid the charge.`
      : `Your free trial ends in ${data.trial_days_left} day${data.trial_days_left !== 1 ? "s" : ""}. Upgrade to keep all your data and features.`;
  }

  if (msg) {
    const style = _BANNER[key];
    return (
      <div style={{
        background: style.bg, border: `1px solid ${style.border}`, borderRadius: 10,
        padding: "10px 18px", marginBottom: 20, display: "flex",
        alignItems: "center", justifyContent: "space-between", gap: 12,
        fontSize: 13, color: style.color, fontWeight: 500,
      }}>
        <span>{msg}</span>
        <a href="/settings?tab=usage" style={{
          flexShrink: 0, color: style.color, fontWeight: 700,
          textDecoration: "underline", cursor: "pointer",
        }}>Upgrade plan →</a>
      </div>
    );
  }

  // Priority 2: proactive quota warnings (>80% of any meter)
  const meters: { meter: string; label: string; used: number; limit: number; pct: number }[] =
    data.meters ?? [];
  const critical = meters.filter(m => m.limit < 9000 && m.pct >= 100);
  const warning  = meters.filter(m => m.limit < 9000 && m.pct >= 80 && m.pct < 100);

  if (critical.length > 0) {
    const names = critical.map(m => m.label).join(", ");
    return (
      <div style={{
        background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10,
        padding: "10px 18px", marginBottom: 20, display: "flex",
        alignItems: "center", justifyContent: "space-between", gap: 12,
        fontSize: 13, color: "#991b1b", fontWeight: 500,
      }}>
        <span>🚫 Monthly limit reached for: <strong>{names}</strong>. These features are blocked until your plan resets or you upgrade.</span>
        <a href="/settings?tab=usage" style={{ flexShrink: 0, color: "#991b1b", fontWeight: 700, textDecoration: "underline" }}>Upgrade →</a>
      </div>
    );
  }

  if (warning.length > 0) {
    const names = warning.map(m => `${m.label} (${m.pct}%)`).join(", ");
    return (
      <div style={{
        background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
        padding: "10px 18px", marginBottom: 20, display: "flex",
        alignItems: "center", justifyContent: "space-between", gap: 12,
        fontSize: 13, color: "#92400e", fontWeight: 500,
      }}>
        <span>⚠️ Approaching monthly limit: <strong>{names}</strong>. Upgrade before you run out.</span>
        <a href="/settings?tab=usage" style={{ flexShrink: 0, color: "#92400e", fontWeight: 700, textDecoration: "underline" }}>View usage →</a>
      </div>
    );
  }

  return null;
}

// ── Onboarding checklist banner (non-blocking, replaces the old wizard) ───────

interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
  link: string;
  hint?: string;
}

interface ChecklistData {
  dismissed: boolean;
  done_count: number;
  total: number;
  items: ChecklistItem[];
}

function OnboardingChecklistBanner() {
  const { user } = useAuth();
  // Workspace setup is a Core concern. Repeating this banner on every module
  // dashboard (and even on upsell pages) made each module home read as another
  // clone of the main dashboard.
  //
  // Shell context, not the raw path: on the System pages the sidebar and badge
  // stay with the module you came from, and a Core setup banner under a FINANCE
  // sidebar would contradict them.
  const inCore = useActiveModule() === null;
  const canSeeChecklist = !!user && ["admin", "admin_assistant"].includes(user.role);
  const { data } = useQuery<ChecklistData>({
    queryKey: ["onboarding-checklist"],
    queryFn: getOnboardingChecklist,
    enabled: canSeeChecklist && inCore,
    staleTime: 60 * 1000,
    retry: false,
  });

  if (!inCore || !data || data.dismissed) return null;

  const items = data.items ?? [];

  return (
    <div style={{
      background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10,
      padding: "14px 18px", marginBottom: 20, fontSize: 13, color: "#166534",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: items.length ? 10 : 0 }}>
        <strong>Finish setting up your workspace — {data.done_count} of {data.total} steps complete</strong>
        <div style={{ display: "flex", gap: 4 }} aria-hidden="true">
          {items.map(item => (
            <span key={item.id} title={item.label} style={{
              width: 8, height: 8, borderRadius: "50%",
              background: item.done ? "#16a34a" : "transparent",
              border: item.done ? "none" : "1px solid #86efac",
            }} />
          ))}
        </div>
      </div>
      {items.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {items.map(item => item.done ? (
            <a key={item.id} href={item.link} style={{
              background: "#ecfdf5", border: "1px solid #bbf7d0", borderRadius: 999,
              padding: "4px 12px", color: "#4d7c62", fontWeight: 500,
              textDecoration: "line-through", fontSize: 12.5,
            }}>✓ {item.label}</a>
          ) : (
            <a key={item.id} href={item.link} title={item.hint} style={{
              display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1,
              background: "#dcfce7", border: "1px solid #86efac", borderRadius: 12,
              padding: "5px 12px", color: "#166534", fontWeight: 600,
              textDecoration: "none", fontSize: 12.5,
            }}>
              <span>{item.label} →</span>
              {item.hint && <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>{item.hint}</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Google login toast ─────────────────────────────────────────────────────────

function GoogleLoginToast() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const email = params.get("google_login");
    if (!email) return;
    toast(`Signed in with Google · ${email}`, "success");
    const next = new URLSearchParams(params);
    next.delete("google_login");
    setParams(next, { replace: true });
  }, []); // run once on mount
  return null;
}

// ── Global billing / quota error listener ──────────────────────────────────────

function BillingErrorListener() {
  const { toast } = useToast();
  useEffect(() => {
    function handler(e: Event) {
      const { code, message } = (e as CustomEvent).detail;
      if (["plan_required", "plan_readonly"].includes(code)) {
        toast(message || "Your account is in read-only mode. Upgrade to continue.", "error");
      } else if (code === "quota_exceeded") {
        toast(message || "Monthly limit reached. Upgrade your plan for more.", "error");
      } else if (code === "employee_limit_reached") {
        toast(message || "Employee limit reached. Upgrade your plan to add more.", "error");
      } else if (code === "plan_expired" || code === "plan_cancelled") {
        toast("Your plan has expired. Please contact support to restore access.", "error");
      } else if (code === "tenant_inactive") {
        toast(message || "Your account has been deactivated. Please contact support.", "error");
      } else if (code === "module_not_enabled") {
        // Safety net only. Navigating to an unowned module renders its upsell in
        // place (see ModuleShell), so this fires just for stray API calls from a
        // Core page that happens to touch a module's data. A toast is the honest
        // response there — yanking someone off the page they are on to a sales
        // pitch, because a background request failed, would be worse than the
        // problem.
        toast(message || "That isn't part of your plan.", "error");
      }
    }
    window.addEventListener("billing:error", handler);
    return () => window.removeEventListener("billing:error", handler);
  }, [toast]);
  return null;
}

// Shared layout wrapper — sidebar + content area
function ShellLayout({ children, role, userName }: React.PropsWithChildren<{ role?: string; userName?: string }>) {
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f1f5f9", fontFamily: "var(--font-sans)" }}>
      <BillingErrorListener />
      <GoogleLoginToast />
      {children}
      <ChatWidget role={role || "employee"} userName={userName} />
    </div>
  );
}

function ShellMain({ children }: React.PropsWithChildren<{}>) {
  return (
    <main className="shell-main" style={{ flex: 1, minHeight: "100vh", padding: "80px 32px 32px", overflowX: "hidden" }}>
      <PinnedAnnouncementBanner />
      <PlanBanner />
      <OnboardingChecklistBanner />
      {children}
    </main>
  );
}

// ── Employee shell ────────────────────────────────────────────────────────
// ── Contractor shell ──────────────────────────────────────────────────────
// ── Admin-assistant shell ─────────────────────────────────────────────────
// ── Finance shell ─────────────────────────────────────────────────────────
// ── Recruiter shell (recruitment + employee personal) ─────────────────────
// ── Full HR/Admin shell ───────────────────────────────────────────────────
// ── Config-driven shell (Phase 3 Package 1) ───────────────────────────────
// Replaces the six role shells above. Not yet reachable — AppShell still
// dispatches to the old ones, and the follow-up PR flips it here and deletes
// them. Landing it dead first keeps the risky change to a one-line diff, with
// equivalence already proven by scripts/extract_config_surface.py.

/** Roles that fetched the dashboard summary. Employee and Contractor did not,
 *  and enabling it for them would add an API call the old shells never made. */
const ROLES_WITH_SUMMARY: RoleKey[] = ["admin_assistant", "finance", "recruiter", "hr"];

function roleKeyFor(role?: string): RoleKey {
  const known: RoleKey[] = ["employee", "contractor", "admin_assistant", "finance", "recruiter", "hr"];
  // admin has no shell of its own — it fell through to HRShell, which then
  // branched internally on role === "admin". Those branches are preserved as
  // "role:admin" guards, so admin maps to the hr config and gets the extras.
  return (known as string[]).includes(role || "") ? (role as RoleKey) : "hr";
}

/**
 * The role-static part of a guard: "role:" tokens only, tenant-level flags
 * assumed true. Module eligibility (spec: module-launcher-role-scope) uses
 * this instead of guardHolds so launcher tiles depend on who you ARE, never
 * on a tenant flag that could flip mid-session — while still excluding
 * routes another role shares this shell config with (admin vs hr).
 */
function roleGuardHolds(guard: string | undefined, user: ShellCtx["user"]): boolean {
  if (!guard) return true;
  return guard.split("+").every((part) =>
    part.startsWith("role:")
      ? part.slice("role:".length).split(",").includes(user?.role)
      : true,
  );
}

/** Evaluate a config guard. "a+b" means every condition must hold.
 *
 *  Tokens are exhaustive: a `role:` membership test, or one of the two tenant-level
 *  flags. There is no open-ended fallback — the per-role feature matrix that used to
 *  supply one was removed (spec: feature-access-removal), and reintroducing a
 *  catch-all is how a typo'd guard silently becomes "visible to everyone".
 *
 *  The `Guard` parameter type is the compile-time half of that: it is the same set
 *  of tokens spelled as a union, so a guard this function cannot resolve is now a
 *  build error rather than a link that disappears in QA. The runtime arm below is
 *  the backstop for the `as Guard` and the untyped caller, and it still fails
 *  closed — but it is no longer the first thing to notice a dead token. */
function guardHolds(guard: Guard | undefined, ctx: ShellCtx): boolean {
  if (!guard) return true;
  return guard.split("+").every((part) => {
    if (part === "timesheetEnabled") return ctx.timesheetEnabled;
    if (part === "legacyInvoicing") return ctx.legacyInvoicingEnabled;
    // "role:admin" or "role:admin,hr" -- membership, not equality. The nav
    // config needs OR over roles (Workspace belongs to admin AND hr), and "+"
    // already means AND, so the alternation lives inside the role token.
    if (part.startsWith("role:")) {
      const allowed = part.slice("role:".length).split(",");
      return allowed.includes(ctx.user?.role);
    }
    if (process.env.NODE_ENV !== "production") {
      throw new Error(`Unknown nav guard token: "${part}"`);
    }
    console.error(`Unknown nav guard token: "${part}" — hiding the entry`);
    return false;
  });
}

/**
 * Narrow the sidebar to the module the user is currently in.
 *
 * An admin who owns everything had 45 links in one flat list. That is the real
 * usability problem here, and it exists independently of billing — nobody
 * navigates a 45-item list, they scan for the one word they want.
 *
 * A previous revision listed every reachable module as a collapsible section so
 * the rest of the product stayed discoverable. It was withdrawn: the collapse
 * state had to persist to stay usable, persisted state outranked "open only the
 * module you are in", and so one toggle left another module's links expanded
 * inside every module forever. Discovery is the launcher's job — it is already
 * role-scoped, always reachable on both desktop and mobile, and costs one click.
 *
 * Three rules:
 *   - a link belonging to a module the tenant does NOT own is dropped entirely
 *     (spec AC14) — it is not theirs to see in the normal nav
 *   - a link belonging to a module this ROLE cannot reach is dropped too,
 *     the same eligibleModules the launcher scopes itself by
 *   - a link belonging to a module they DO own shows only while they are in that
 *     module; the launcher is how they move between modules
 *
 * Core's OWN pages are not pinned into modules either. A single "PEOPLE →
 * Dashboard" link used to ride along in every module sidebar as the way back;
 * it read as though People were a sub-section of Finance, and it is not — People
 * is a product you switch to, on the same footing as the modules. Two routes
 * back survive without it and neither is a nav link: the sidebar logo is a
 * NavLink to "/", and the launcher's first tile is People (CORE_LABEL → "/"),
 * mounted on both desktop and mobile. So inside a module the sidebar is that
 * module plus System, nothing else.
 */
function scopeNavToModule(
  entries: ReturnType<typeof buildShellConfig>[RoleKey]["nav"],
  activeModule: ModuleKey | null,
  modules: ModuleMap | undefined,
  eligibleModules: Set<ModuleKey>,
): NavItem[] {
  // The module's own dashboard, first in its section. Labelled "Dashboard"
  // because that is what it is -- calling it "Overview" next to Core's
  // "Dashboard" made two dashboard-ish links sit together meaning different
  // things.
  //
  // Gated on eligibility as well as ownership, the same as the module's other
  // links below. activeModule comes from the URL, so a role with no routes into
  // a module can still land on its path by typing it; without this the sidebar
  // would offer them a dashboard the route guard is about to bounce them from.
  const moduleDashboardLink: NavItem[] =
    activeModule && ownsModule(modules, activeModule) && eligibleModules.has(activeModule)
      ? [{ to: MODULE_HOME_PATHS[activeModule], label: "Dashboard", end: true } as NavItem]
      : [];

  // Which section each link sits under in the role's config. System-section
  // links (Settings/Workspace/Security — already RBAC-filtered by guardHolds
  // upstream) stay visible in EVERY module context: admin things are not a
  // module's concern, and hiding them made "where did Settings go?" the first
  // question after entering a module.
  let sectionCursor = "";
  const sectionOf = new Map<string, string>();
  for (const entry of entries) {
    if ("section" in entry) sectionCursor = entry.section as string;
    else sectionOf.set(entry.to, sectionCursor);
  }
  const isSystem = (to: string) => sectionOf.get(to) === "System";

  const visible = entries.filter((entry) => {
    if ("section" in entry) return true;
    const owner = moduleForPath(entry.to);

    if (!owner) {
      // Core. Shown in full when Core IS the context; inside a module only the
      // System group survives, so entering a module actually narrows the list.
      // Keeping all 22 Core links alongside a module's own would give 29 items —
      // barely better than the 45 this package exists to fix.
      return activeModule === null || isSystem(entry.to);
    }
    if (!ownsModule(modules, owner)) return false;      // not bought — hide
    if (!eligibleModules.has(owner)) return false;      // role cannot reach it — hide
    return owner === activeModule;                      // owned — only in context
  });

  const bare = (entry: { to: string; label: React.ReactNode; end?: boolean }) =>
    ({ to: entry.to, label: entry.label, end: entry.end } as NavItem);

  // Inside a module, the original section headers are wrong by construction:
  // they are Core-nav groupings, so Payroll's link arrives under "FINANCE" and
  // Performance's under "PEOPLE". Rebuild the list to mirror Core's own shape —
  // Dashboard first, then a section named after the module, then its children:
  //   Dashboard / FINANCE / Invoices … / SYSTEM / Settings …
  if (activeModule !== null) {
    const linksOnly = visible.filter((e) => !("section" in e)) as
      Array<{ to: string; label: React.ReactNode; end?: boolean }>;
    const moduleLinks = linksOnly.filter(
      (e) => moduleForPath(e.to) === activeModule && e.to !== MODULE_HOME_PATHS[activeModule],
    );
    const systemLinks = linksOnly.filter(
      (e) => moduleForPath(e.to) === null && isSystem(e.to),
    );
    return [
      ...moduleDashboardLink,
      ...(moduleLinks.length
        ? [{ section: MODULE_LABELS[activeModule], accent: MODULE_COLORS[activeModule] } as NavItem]
        : []),
      ...moduleLinks.map(bare),
      // System keeps its own header and sits last, same position as in Core.
      ...(systemLinks.length ? [{ section: "System" } as NavItem] : []),
      ...systemLinks.map(bare),
    ];
  }

  // Core context keeps its real groupings. A divider with nothing under it is
  // noise. Drop any section immediately followed by another section or by the
  // end of the list.
  const scoped: NavItem[] = visible
    .filter((entry, i) => {
      if (!("section" in entry)) return true;
      const next = visible[i + 1];
      return next !== undefined && !("section" in next);
    })
    .map((entry) => ("section" in entry
      ? ({ section: entry.section } as NavItem)
      : bare(entry)));

  return [...moduleDashboardLink, ...scoped];
}


/** Names the module the sidebar is currently scoped to.
 *
 *  Once the sidebar shows a subset of the product, "which subset am I looking
 *  at?" has to be answerable without reading the links and inferring it. Core is
 *  labelled "People" — its user-facing name — rather than left blank, so the
 *  answer is never absence. No "MODULE" eyebrow above it: the badge sits alone
 *  at the top of the sidebar and the coloured dot already reads as a scope
 *  marker, so the word only added a row of chrome to every screen. */
function ModuleBadge({ module }: { module: ModuleKey | null }) {
  const label = module ? MODULE_LABELS[module] : CORE_LABEL;
  const color = module ? MODULE_COLORS[module] : "#38bdf8";
  return (
    <div style={{
      margin: "4px 10px 8px", padding: "9px 10px", borderRadius: 8,
      background: "#17233c", border: "1px solid #26324a",
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: "#fff", fontWeight: 700 }}>{label}</span>
    </div>
  );
}

function ModuleShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const roleKey = roleKeyFor(user?.role);

  const { modules } = useModules();

  const { data: branding } = useQuery({ queryKey: ["branding"], queryFn: getBranding, enabled: !!user, staleTime: 5 * 60 * 1000 });
  const { data: summary } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: getDashboardSummary,
    enabled: !!user && ROLES_WITH_SUMMARY.includes(roleKey),
    refetchInterval: 60_000,
  });

  const companyName: string = branding?.company_name || "HRxAI";
  const logoUrl = getBrandingLogoUrl();
  const timesheetEnabled = branding?.timesheet_enabled === "true";
  const legacyInvoicingEnabled = branding?.legacy_invoicing_enabled === "true";

  const ctx: ShellCtx = { user, timesheetEnabled, legacyInvoicingEnabled, summary };
  const def = buildShellConfig(ctx)[roleKey];

  // Not moduleForPath(pathname) directly: the System links (Settings,
  // Workspace, Security) are Core-owned but ride along in every module sidebar,
  // so deriving context from them would eject you from the module you are in.
  const activeModule = useActiveModule();

  // Modules this ROLE can use — entitlements are tenant-wide, so without this
  // an employee gets offered Payroll in the launcher and lands on an operator
  // dashboard whose every number 403s (spec: module-launcher-role-scope).
  const eligibleModules = modulesForRoutes(
    def.routes.filter((r) => roleGuardHolds(r.guard, user)).map((r) => r.path),
  );

  // Remember where they were, so "last module I used" has something to point at.
  React.useEffect(() => {
    rememberLastModule(user?.id, activeModule);
  }, [user?.id, activeModule]);

  // Honour the landing preference, once, on arrival at Core. Only redirects when
  // the preference is "last used" AND that module is still owned — landing
  // straight on an upsell because a module lapsed between sessions would be a
  // hostile way to open the app.
  const landingApplied = React.useRef(false);
  React.useEffect(() => {
    if (landingApplied.current || !user || !modules) return;
    landingApplied.current = true;
    if (location.pathname !== "/") return;
    // Eligibility too, not just ownership: a stale "last used" preference
    // (e.g. saved before role scoping shipped) must not land anyone on a
    // module home that now renders AccessDenied for them.
    const target = resolveLandingModule(user.id, modules);
    if (target && eligibleModules.has(target)) navigate(MODULE_HOME_PATHS[target], { replace: true });
  }, [user, modules, location.pathname, navigate]);
  const links: NavItem[] = scopeNavToModule(
    def.nav.filter((entry) => guardHolds(entry.guard, ctx)),
    activeModule,
    modules,
    eligibleModules,
  );

  // useRoutes rather than <Routes> with mapped children: it takes plain route
  // objects, which is the idiomatic way to render a route list built at runtime
  // and avoids handing React keys to Route as props.
  // A route belonging to an unowned module renders its upsell in place, at its
  // own URL. Not a redirect: the address bar, the back button and the nav
  // position all stay put, and only the body differs. Deep links keep working —
  // someone who bookmarked /jobs before the module lapsed lands on an offer to
  // restore it, not a 403 or a 404.
  // Overview routes for the two multi-page modules. Added here rather than in
  // shellConfig because they are not a per-role concern — anyone who owns the
  // module gets them, and the ownership filter below handles the rest.
  // Every module has a dashboard at its own route, and every one of them is now a real
  // page. AI Expense Capture was the last holdout on a generic fallback that rendered a
  // heading and two buttons; that component is gone, so this map is total rather than
  // partial and there is no fallback branch left to take.
  // Eligibility outranks ownership: a role with no pages in a module gets
  // AccessDenied at its home path — not the dashboard (hollow, every call
  // 403s) and not the upsell (they are not the buyer).
  const MODULE_DASHBOARD_PAGES: Record<ModuleKey, React.ReactElement> = {
    finance: <FinanceDashboardPage />,
    recruitment: <RecruitmentDashboardPage />,
    payroll: <PayrollDashboardPage />,
    esign: <EsignDashboardPage />,
    performance: <PerformanceDashboardPage />,
    ai_expense_capture: <ExpenseCaptureDashboardPage />,
  };
  const moduleDashboardRoutes = MODULE_KEYS.map((key) => ({
    path: MODULE_HOME_PATHS[key],
    element: !eligibleModules.has(key)
      ? <AccessDenied />
      : !ownsModule(modules, key)
        ? <ModuleUpsellPage module={key} />
        : MODULE_DASHBOARD_PAGES[key],
  }));

  const routeElement = useRoutes([
    ...moduleDashboardRoutes,
    ...def.routes
      .filter((route) => guardHolds(route.guard, ctx))
      .map((route) => {
        const owner = moduleForPath(route.path);
        const locked = owner !== null && !ownsModule(modules, owner);
        return {
          path: route.path,
          element: locked ? <ModuleUpsellPage module={owner as ModuleKey} /> : route.element,
        };
      }),
  ]);

  return (
    <ShellLayout role={user?.role} userName={user?.name}>
      <AppNav
        links={links}
        user={user}
        logout={logout}
        companyName={companyName}
        logoUrl={logoUrl}
        LogoMark={NavLogoMark}
        moduleBadge={<ModuleBadge module={activeModule} />}
        mobileSwitcher={
          <ProductSwitcher
            modules={modules}
            eligible={eligibleModules}
            activeModule={activeModule}
            userId={user?.id}
            anchor="left"
            dark
          />
        }
      />
      <ShellHeader eligibleModules={eligibleModules} />
      <ShellMain>{routeElement}</ShellMain>
    </ShellLayout>
  );
}

// ── AppShell dispatcher ───────────────────────────────────────────────────
function AppShell() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
      <p style={{ color: "#64748b" }}>Loading...</p>
    </div>
  );
  if (!user) return <LandingPage />;
  if (user.requires_2fa_enrollment) return <EnrollmentGatePage />;
  if (user.is_super_admin) return <SuperAdminShell />;
  // One shell for every role. roleKeyFor maps the role to its config, including
  // admin, which never had a shell of its own and fell through to HRShell.
  return <ModuleShell />;
}

function NavLogoMark({ logoUrl, companyName }: { logoUrl: string; companyName: string }) {
  const [hasLogo, setHasLogo] = React.useState(false);
  React.useEffect(() => {
    const img = new Image();
    img.onload = () => setHasLogo(true);
    img.onerror = () => setHasLogo(false);
    img.src = logoUrl;
  }, [logoUrl]);

  if (hasLogo) {
    return <img src={logoUrl} alt={companyName} style={{ height: 32, maxWidth: 80, objectFit: "contain", borderRadius: 4 }} />;
  }
  return (
    <div style={{
      width: 32, height: 32, background: "linear-gradient(135deg, #00c9a7, #1e3a8a)",
      borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 16, fontWeight: "bold", color: "white",
    }}>{companyName[0]?.toUpperCase() || "H"}</div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
          <ConfirmProvider>
            <Routes>
              <Route path="/login" element={<LoginWrapper />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/signup/success" element={<SignupSuccessPage />} />
              <Route path="/request-access" element={<RequestAccessPage />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/setup/:token" element={<SetupLinkPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/billing/success" element={<BillingSuccessPage />} />
              <Route path="/careers/:slug" element={<CareersPage />} />
              <Route path="/screen/:token" element={<ScreenPublicPage />} />
              <Route path="/interview-confirm/:token" element={<InterviewConfirmPage />} />
              <Route path="/interview-availability/:token" element={<InterviewAvailabilityPage />} />
              <Route path="/offer/:token" element={<OfferAcceptPage />} />
              <Route path="/offer-approval/:token" element={<OfferApprovalPage />} />
              <Route path="/esign-sign/:token" element={<PublicEsignSignPage />} />
              <Route path="/docs" element={<DocsIndex />} />
              <Route path="/docs/getting-started" element={<DocsGettingStarted />} />
              <Route path="/docs/ai-assistant" element={<DocsAIAssistant />} />
              <Route path="/docs/recruitment" element={<DocsRecruitment />} />
              <Route path="/docs/people-hr" element={<DocsPeopleHR />} />
              <Route path="/docs/finance" element={<DocsFinance />} />
              <Route path="/docs/admin-settings" element={<DocsAdminSettings />} />
              <Route path="/*" element={<AppShell />} />
            </Routes>
          </ConfirmProvider>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function LoginWrapper() {
  const [params] = useSearchParams();
  return <LoginPage error={params.get("error") || undefined} />;
}
